/**
 * Compaction Manager
 * Handles CRDT compaction scheduling and triggers.
 * Extracted from websocket.js for better separation of concerns.
 *
 * Compaction sends the full CRDT state to replace accumulated deltas,
 * reducing storage and improving sync performance for new clients.
 */

const MAX_CONSECUTIVE_FAILURES = 3;
// Wait this long for the server's CompactionAck before giving up and
// leaving deltaCount as-is: the next trigger retries.
const ACK_TIMEOUT_MS = 30000;
// Leaving a map (tab hidden) compacts only after this many of this client's
// own changes since its last compaction.
const LEAVING_MIN_CHANGES = 100;

// When a map is compacted (docs/architecture/sync.md, "Compaction"): when the
// server asks (from 1,000 stored changes, one writer per 250), after 500 of
// this client's own changes, when the person leaves a map they changed a lot,
// at the 5,000 cap. No longer every 10 minutes per editor (until 2026-10-18):
// each editor uploaded the whole map every 10 minutes, five copies for five
// editors, colliding ("map changed during compaction"); on staging at the busy
// hour of 30,000 users, 15 times more compactions than needed. Compaction is
// upkeep (the 5,000 cap, opening speed, storage), not durability: the base and
// the stored changes are the whole map, and fewer compactions mean fewer
// deletions on the server. A failed one is retried by the next trigger (the
// server asks again 250 changes later).

/**
 * Create a compaction manager
 * @param {Object} options
 * @param {number} options.deltaThreshold - Delta count before forcing compaction (default 100)
 * @param {Function} options.onCompact - Async callback to perform compaction (receives reason string)
 * @param {Function} options.onFailure - Callback when compaction fails persistently (receives {failures, lastError})
 * @param {Function} options.log - Callback for logging (optional)
 * @returns {Object} Compaction manager API
 */
export function createCompactionManager(options) {
  const {
    deltaThreshold = 100,
    onCompact,
    onFailure,
    // After a snapshot is stored (ack received, no compaction pending any
    // more): it holds every change made before it, so the channel can see
    // whether everything it made is now on the server.
    onStored,
    log = () => {},
  } = options;

  // Internal state
  let deltaCount = 0;
  let lastCompactionTime = 0;
  let pendingCompaction = false;
  let visibilityHandler = null;
  let consecutiveFailures = 0;
  let lastError = null;
  let failureNotified = false;
  // When set, all compaction is blocked. Used when this client's local state
  // is known-incomplete (e.g. some server deltas failed to decrypt during
  // sync): compacting would replace the server's delta rows with a full
  // state that lacks the skipped deltas' content — permanent loss if no
  // other live client re-pushes them. Cleared by a subsequent clean sync.
  let suppressedReason = null;
  // Resolver/rejecter for the in-flight ack promise, set while a compaction
  // is waiting for the server's CompactionAck. onAck / onServerError fire it.
  let ackPending = null;

  /**
   * Handle visibility change - compact when tab becomes hidden
   */
  function handleVisibilityChange() {
    if (document.hidden && deltaCount >= LEAVING_MIN_CHANGES && !pendingCompaction) {
      log("Tab hidden after many changes, triggering compaction");
      triggerCompaction("visibility");
    }
  }

  /**
   * Start tracking (and the visibility listener)
   */
  function start() {
    stop(); // Clear any existing listener
    lastCompactionTime = Date.now();
    deltaCount = 0;
    consecutiveFailures = 0;
    lastError = null;
    failureNotified = false;

    // Compact when tab becomes hidden (user switching away), after many changes
    visibilityHandler = handleVisibilityChange;
    document.addEventListener("visibilitychange", visibilityHandler);
  }

  /**
   * Stop: remove the visibility listener
   */
  function stop() {
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
  }

  /**
   * Record that a delta was sent (increment counter)
   */
  function recordDelta() {
    deltaCount++;
  }

  /**
   * Check if compaction is needed based on delta count
   * @returns {boolean}
   */
  function isCompactionNeeded() {
    return deltaCount >= deltaThreshold;
  }

  /**
   * Get current delta count
   * @returns {number}
   */
  function getDeltaCount() {
    return deltaCount;
  }

  /**
   * Reset delta count (call after compaction completes)
   */
  function resetDeltaCount() {
    deltaCount = 0;
    lastCompactionTime = Date.now();
  }

  /**
   * Get pending compaction status
   * @returns {boolean}
   */
  function getPendingCompaction() {
    return pendingCompaction;
  }

  /**
   * Set pending compaction status
   * @param {boolean} value
   */
  function setPendingCompaction(value) {
    pendingCompaction = value;
  }

  /**
   * Called when the server acknowledges a compaction.
   * Resolves the in-flight ack promise (which releases triggerCompaction to
   * commit the deltaCount reset) and resets failure tracking.
   */
  function onAck() {
    consecutiveFailures = 0;
    lastError = null;
    failureNotified = false;
    if (ackPending) {
      const p = ackPending;
      ackPending = null;
      p.resolve();
    }
  }

  /**
   * Called when a server error is received after a compaction attempt.
   * Rejects the in-flight ack promise so triggerCompaction leaves
   * deltaCount intact and the next trigger retries.
   * @param {string} errorMessage - Error message from server
   */
  function onServerError(errorMessage) {
    if (/quota_exceeded|storage quota exceeded/i.test(String(errorMessage))) {
      if (ackPending) { const pending = ackPending; ackPending = null; pending.reject(new Error(errorMessage)); }
      return;
    }
    // Only count as compaction failure if we recently sent a compaction
    if (!pendingCompaction && Date.now() - lastCompactionTime > 5000) return;

    consecutiveFailures++;
    lastError = errorMessage;
    log(`Compaction server error (failure ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}): ${errorMessage}`);

    if (ackPending) {
      const p = ackPending;
      ackPending = null;
      p.reject(new Error(errorMessage));
    }

    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !failureNotified) {
      failureNotified = true;
      if (onFailure) {
        onFailure({ failures: consecutiveFailures, lastError });
      }
    }
  }

  /**
   * The server refused the snapshot because it does not cover every stored
   * update (one arrived after our sync) — not a failure: the caller syncs
   * again and the next tick retries with fresh coverage.
   * @param {string} errorMessage
   */
  function onCoverageRejected(errorMessage) {
    log(`Compaction refused (${errorMessage}); will retry after a new sync`);
    if (ackPending) {
      const p = ackPending;
      ackPending = null;
      p.reject(new Error(errorMessage));
    }
  }

  /**
   * The server was busy and stored nothing (backend save_gate.rs) — not a
   * failure: the next tick retries, with everything since.
   * @param {string} errorMessage
   */
  function onBusy(errorMessage) {
    log(`Compaction refused (${errorMessage}); will retry later`);
    if (ackPending) {
      const p = ackPending;
      ackPending = null;
      p.reject(new Error(errorMessage));
    }
  }

  /**
   * Trigger a compaction (send full state to server)
   * @param {string} reason - Why compaction was triggered
   * @returns {Promise<boolean>} True if compaction was sent
   */
  async function triggerCompaction(reason) {
    const stored = await compactNow(reason);
    if (stored) onStored?.();
    return stored;
  }

  async function compactNow(reason) {
    if (suppressedReason) {
      log(`Compaction blocked (${suppressedReason}) — local state may be incomplete, not overwriting server`);
      return false;
    }
    if (pendingCompaction) {
      return false;
    }

    if (!onCompact) {
      log("No onCompact callback configured");
      return false;
    }

    pendingCompaction = true;
    // Snapshot the counter at send time: deltas that accrue during the
    // round-trip must not be zeroed; they belong to the next compaction window.
    const deltasSent = deltaCount;
    log(`Triggering compaction (reason: ${reason}, deltas: ${deltasSent})`);

    // Arm the ack waiter BEFORE sending so we can't miss a fast ack.
    const ackWait = new Promise((resolve, reject) => {
      ackPending = { resolve, reject };
    });

    try {
      const result = await onCompact(reason);
      if (!result) {
        // Send itself failed (e.g., WS not open). Don't wait for an ack
        // that's never coming; leave deltaCount intact for the next tick.
        if (ackPending) { ackPending = null; }
        return false;
      }

      // Wait for the server's CompactionAck before committing the reset.
      // If the server rejects (rate limit, version mismatch, DB error) or
      // the ack never arrives, keep deltaCount so the next trigger retries.
      let timeoutId;
      const timeout = new Promise((_, rej) => {
        timeoutId = setTimeout(
          () => rej(new Error("compaction ack timeout")),
          ACK_TIMEOUT_MS,
        );
      });
      try {
        await Promise.race([ackWait, timeout]);
        deltaCount = Math.max(0, deltaCount - deltasSent);
        lastCompactionTime = Date.now();
        return true;
      } catch (err) {
        const alreadyCounted = ackPending === null;
        if (!alreadyCounted) {
          consecutiveFailures++;
          lastError = err.message || String(err);
          log(`Compaction ack failed (failure ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}): ${lastError}`);
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !failureNotified) {
            failureNotified = true;
            if (onFailure) {
              onFailure({ failures: consecutiveFailures, lastError });
            }
          }
        }
        log(`Compaction ack not received: ${err.message} — deltaCount retained for retry`);
        // onServerError handles server-reject failure counting before rejecting ackWait.
        return false;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (err) {
      if (ackPending) { ackPending = null; }
      consecutiveFailures++;
      lastError = err.message || String(err);
      log(`Compaction failed (failure ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}): ${lastError}`);

      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !failureNotified) {
        failureNotified = true;
        if (onFailure) {
          onFailure({ failures: consecutiveFailures, lastError });
        }
      }
      return false;
    } finally {
      // Belt-and-braces: any path that didn't already consume ackPending.
      if (ackPending) { ackPending = null; }
      pendingCompaction = false;
    }
  }

  /**
   * Get time of last compaction
   * @returns {number} Timestamp
   */
  function getLastCompactionTime() {
    return lastCompactionTime;
  }

  /**
   * Block all compaction until the next clean sync (see suppressedReason).
   * @param {string} reason
   */
  function suppress(reason) {
    suppressedReason = reason;
    log(`Compaction suppressed: ${reason}`);
  }

  /** Re-allow compaction after a clean sync. */
  function unsuppress() {
    if (suppressedReason) log(`Compaction suppression cleared (was: ${suppressedReason})`);
    suppressedReason = null;
  }

  return {
    start,
    stop,
    suppress,
    unsuppress,
    recordDelta,
    isCompactionNeeded,
    getDeltaCount,
    resetDeltaCount,
    getPendingCompaction,
    setPendingCompaction,
    triggerCompaction,
    getLastCompactionTime,
    onAck,
    onServerError,
    onBusy,
    onCoverageRejected,
  };
}
